import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, Box, Typography, Divider, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Stack,
  Switch, CircularProgress, Alert, Autocomplete,
  IconButton, Paper, FormControlLabel, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Group as TeamIcon,
  LocalOffer as TagIcon,
  Email as EmailIcon,
  NotificationsActive as AlertLevelIcon,
  ArrowForward as ArrowIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

// ─── Auth helper ──────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Template { template_id: string; template_name: string; }
interface AssetType { asset_type_id: string; type_name: string; }
interface Asset { asset_id: string; asset_name: string; serial_number?: string; location_name?: string; }
interface Location { locationId: string; locationName: string; }
interface User { user_id: string; first_name: string; last_name: string; email: string; }
interface Team { group_id: string; group_name: string; }
interface JobTag { job_tag_id: string; tag_name: string; }

export interface Recipient {
  recipient_id?: string;
  recipient_type: 'user' | 'team' | 'job_tag' | 'email';
  user_id?: string;
  team_id?: string;
  job_tag_id?: string;
  /** Free-form address when recipient_type is `email` */
  email?: string;
  _label?: string;
}

export interface AlertLevel {
  alert_level_id?: string;
  minutes_after_scheduled_time: number;
  alert_type: 'warning' | 'critical' | 'urgent';
  recipients: Recipient[];
}

export type RecurrenceType = 'daily' | 'weekly' | 'monthly_date' | 'monthly_weekday' | 'interval' | 'yearly';

export interface ScheduleData {
  schedule_id?: string;
  template_id: string;
  template_name?: string;
  asset_type_ids: string[];
  scope_asset_ids: string[];
  scope_job_tag_ids: string[];
  scope_tag_template_ids: string[];
  location_id: string | null;
  schedule_name: string;
  /** Recurrence pattern — defaults to 'weekly' for backward compatibility */
  recurrence_type: RecurrenceType;
  /** weekly: which days (0=Sun … 6=Sat) */
  days_of_week: number[];
  /** monthly_date: 1–31, or -1 for last day of month */
  day_of_month: number;
  /** monthly_weekday: 1=first, 2=second, 3=third, 4=fourth, -1=last */
  week_of_month: number;
  /** monthly_weekday: 0=Sun … 6=Sat */
  weekday: number;
  /** interval: repeat every N units */
  interval_amount: number;
  interval_unit: 'days' | 'weeks' | 'months';
  /** yearly: 0=Jan … 11=Dec */
  month_of_year: number;
  /** yearly: 1–31 */
  yearly_day: number;
  time_of_day: string;
  timezone: string;
  enabled: boolean;
  email_notifications_enabled: boolean;
  push_notifications_enabled: boolean;
  send_roundup_email: boolean;
  roundup_lookback_amount: number;
  roundup_lookback_unit: 'hours' | 'days' | 'weeks' | 'months';
  alert_levels: AlertLevel[];
  roundup_recipients: Recipient[];
}

interface Props {
  open: boolean;
  schedule: ScheduleData | null;
  onClose: () => void;
  onSaved: () => void;
  /** When false the "By location" scope option is hidden (Sites / Locations module is off) */
  locationsEnabled?: boolean;
  /** "Site" or "Location" — used to label the location scope option */
  locationLabel?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const RECURRENCE_OPTS: { value: RecurrenceType; label: string; desc: string }[] = [
  { value: 'daily',            label: 'Daily',             desc: 'Every day' },
  { value: 'weekly',           label: 'Weekly',            desc: 'Choose specific days' },
  { value: 'monthly_date',     label: 'Monthly (date)',    desc: 'e.g. 1st of every month' },
  { value: 'monthly_weekday',  label: 'Monthly (weekday)', desc: 'e.g. first Monday' },
  { value: 'interval',         label: 'Every N',           desc: 'e.g. every 2 weeks' },
  { value: 'yearly',           label: 'Yearly',            desc: 'Once a year' },
];

export function describeRecurrence(s: {
  recurrence_type?: RecurrenceType;
  days_of_week?: number[];
  day_of_month?: number;
  week_of_month?: number;
  weekday?: number;
  interval_amount?: number;
  interval_unit?: string;
  month_of_year?: number;
  yearly_day?: number;
}): string {
  const rt = s.recurrence_type ?? 'weekly';
  const WEEK_ORD: Record<number, string> = { 1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', [-1]: 'Last' };
  switch (rt) {
    case 'daily':
      return 'Every day';
    case 'weekly': {
      const days = (s.days_of_week ?? []).map(d => DAY_LABELS[d]).join(', ');
      return days || 'Weekly';
    }
    case 'monthly_date':
      if ((s.day_of_month ?? 1) === -1) return 'Last day of every month';
      return `${ordinal(s.day_of_month ?? 1)} of every month`;
    case 'monthly_weekday': {
      const wo = WEEK_ORD[s.week_of_month ?? 1] ?? 'First';
      const dn = FULL_DAY_NAMES[s.weekday ?? 1];
      return `${wo} ${dn} of every month`;
    }
    case 'interval':
      return `Every ${s.interval_amount ?? 2} ${s.interval_unit ?? 'weeks'}`;
    case 'yearly': {
      const mn = MONTH_NAMES_SHORT[s.month_of_year ?? 0];
      return `${mn} ${ordinal(s.yearly_day ?? 1)} each year`;
    }
    default:
      return (s.days_of_week ?? []).map(d => DAY_LABELS[d]).join(', ') || 'Weekly';
  }
}

const ALERT_TYPE_OPTS: { value: 'warning' | 'critical' | 'urgent'; label: string; color: string; bg: string }[] = [
  { value: 'warning', label: 'Warning',  color: '#D97706', bg: '#FEF9C3' },
  { value: 'critical', label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
  { value: 'urgent', label: 'Urgent',   color: '#7C3AED', bg: '#EDE9FE' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyForm = (): ScheduleData => ({
  template_id: '',
  asset_type_ids: [],
  scope_asset_ids: [],
  scope_job_tag_ids: [],
  scope_tag_template_ids: [],
  location_id: null,
  schedule_name: '',
  recurrence_type: 'weekly',
  days_of_week: [1, 2, 3, 4, 5],
  day_of_month: 1,
  week_of_month: 1,
  weekday: 1,
  interval_amount: 2,
  interval_unit: 'weeks',
  month_of_year: 0,
  yearly_day: 1,
  time_of_day: '08:00',
  timezone: 'Europe/London',
  enabled: true,
  email_notifications_enabled: true,
  push_notifications_enabled: true,
  send_roundup_email: false,
  roundup_lookback_amount: 24,
  roundup_lookback_unit: 'hours',
  alert_levels: [{ minutes_after_scheduled_time: 15, alert_type: 'warning', recipients: [] }],
  roundup_recipients: [],
});

function normalizeUser(raw: Record<string, unknown>): User {
  return {
    user_id: String(raw.user_id ?? raw.userId ?? ''),
    first_name: String(raw.first_name ?? raw.firstName ?? ''),
    last_name: String(raw.last_name ?? raw.lastName ?? ''),
    email: String(raw.email ?? ''),
  };
}

function normalizeTeam(raw: Record<string, unknown>): Team {
  return {
    group_id: String(raw.group_id ?? raw.groupId ?? ''),
    group_name: String(raw.group_name ?? raw.groupName ?? ''),
  };
}

function unwrapList(res: { data?: unknown }): unknown[] {
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object' && Array.isArray((d as { data?: unknown }).data)) return (d as { data: unknown[] }).data;
  return [];
}

function labelFor(r: Recipient): string {
  if (r._label) return r._label;
  if (r.recipient_type === 'email' && r.email) return r.email;
  return r.user_id ? 'User' : r.team_id ? 'Team' : 'Tag';
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Inline recipient picker (reused for each alert level and roundup) ────────

interface RecipientPickerProps {
  recipients: Recipient[];
  onChange: (recs: Recipient[]) => void;
  users: User[];
  teams: Team[];
  jobTags: JobTag[];
  label?: string;
  /** When no chips yet — differs for escalation vs schedule round-up */
  emptyHint?: string;
}

const RecipientPicker: React.FC<RecipientPickerProps> = ({
  recipients, onChange, users, teams, jobTags, label, emptyHint,
}) => {
  const [tab, setTab] = useState<'user' | 'team' | 'job_tag' | 'email'>('user');
  const [freeEmail, setFreeEmail] = useState('');

  const addUser = (u: User | null) => {
    if (!u) return;
    if (recipients.some(r => r.user_id === u.user_id)) return;
    onChange([...recipients, { recipient_type: 'user', user_id: u.user_id, _label: `${u.first_name} ${u.last_name}`.trim() || u.email }]);
  };
  const addTeam = (t: Team | null) => {
    if (!t) return;
    if (recipients.some(r => r.team_id === t.group_id)) return;
    onChange([...recipients, { recipient_type: 'team', team_id: t.group_id, _label: t.group_name }]);
  };
  const addTag = (j: JobTag | null) => {
    if (!j) return;
    if (recipients.some(r => r.job_tag_id === j.job_tag_id)) return;
    onChange([...recipients, { recipient_type: 'job_tag', job_tag_id: j.job_tag_id, _label: j.tag_name }]);
  };
  const addEmail = () => {
    const trimmed = freeEmail.trim().toLowerCase();
    if (!trimmed || !EMAIL_RX.test(trimmed)) return;
    if (recipients.some(r => r.recipient_type === 'email' && r.email?.toLowerCase() === trimmed)) return;
    onChange([...recipients, { recipient_type: 'email', email: trimmed, _label: trimmed }]);
    setFreeEmail('');
  };
  const remove = (idx: number) => onChange(recipients.filter((_, i) => i !== idx));

  return (
    <Box>
      {label && (
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {label}
        </Typography>
      )}

      {/* Tab selector */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {(['user', 'team', 'job_tag', 'email'] as const).map(t => (
          <Chip
            key={t}
            icon={
              t === 'user' ? <PersonIcon sx={{ fontSize: 14 }} />
                : t === 'team' ? <TeamIcon sx={{ fontSize: 14 }} />
                  : t === 'job_tag' ? <TagIcon sx={{ fontSize: 14 }} />
                    : <EmailIcon sx={{ fontSize: 14 }} />
            }
            label={t === 'user' ? 'User' : t === 'team' ? 'Team' : t === 'job_tag' ? 'Tag' : 'Email'}
            size="small"
            variant={tab === t ? 'filled' : 'outlined'}
            color={tab === t ? 'primary' : 'default'}
            onClick={() => setTab(t)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {tab === 'user' && (
        <Autocomplete
          size="small"
          options={users}
          getOptionLabel={u => `${u.first_name} ${u.last_name}`.trim() || u.email}
          renderOption={(props, u) => (
            <li {...props} key={u.user_id}>
              <Box>
                <Typography variant="body2">{`${u.first_name} ${u.last_name}`.trim() || u.email}</Typography>
                <Typography variant="caption" color="text.secondary">{u.email}</Typography>
              </Box>
            </li>
          )}
          onChange={(_, v) => addUser(v)}
          renderInput={p => <TextField {...p} placeholder="Search users…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
        />
      )}
      {tab === 'team' && (
        <Autocomplete
          size="small"
          options={teams}
          getOptionLabel={t => t.group_name}
          onChange={(_, v) => addTeam(v)}
          renderInput={p => <TextField {...p} placeholder="Search teams…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.group_id === b.group_id}
        />
      )}
      {tab === 'job_tag' && (
        <Autocomplete
          size="small"
          options={jobTags}
          getOptionLabel={j => j.tag_name}
          onChange={(_, v) => addTag(v)}
          renderInput={p => <TextField {...p} placeholder="Search tags…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.job_tag_id === b.job_tag_id}
        />
      )}
      {tab === 'email' && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            type="email"
            label="External email"
            placeholder="name@company.com"
            value={freeEmail}
            onChange={e => setFreeEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
          />
          <Button variant="outlined" size="small" onClick={addEmail} sx={{ mt: 0.5, flexShrink: 0 }}>
            Add
          </Button>
        </Stack>
      )}

      {/* Chips */}
      {recipients.length > 0 ? (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {recipients.map((r, i) => (
            <Chip
              key={i}
              label={labelFor(r)}
              size="small"
              onDelete={() => remove(i)}
              color={
                r.recipient_type === 'user' ? 'primary'
                  : r.recipient_type === 'team' ? 'secondary'
                    : r.recipient_type === 'email' ? 'info'
                      : 'default'
              }
              variant="outlined"
            />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {emptyHint ?? 'No recipients added — escalation alerts will still run but won\'t notify anyone.'}
        </Typography>
      )}
    </Box>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const CommScheduleDrawer: React.FC<Props> = ({ open, schedule, onClose, onSaved, locationsEnabled = true, locationLabel = 'Location' }) => {
  const [form, setForm] = useState<ScheduleData>(emptyForm());
  const [assetScope, setAssetScope] = useState<'all' | 'asset_type' | 'asset_list' | 'location' | 'asset_tag'>('asset_type');

  // Reference data
  const [templates, setTemplates] = useState<Template[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingRef, setLoadingRef] = useState(false);

  // ── Load reference data ──────────────────────────────────────────────────

  const loadRefData = useCallback(async () => {
    setLoadingRef(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/check-templates`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/asset-types`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/assets`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/locations`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/teams`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/job-tags`, { headers: getAuthHeaders() }),
      ]);
      const [tmpl, at, assetRes, loc, uRes, teamRes, jtRes] = results;
      if (tmpl.status === 'fulfilled') {
        const d = tmpl.value.data;
        setTemplates(Array.isArray(d) ? d : d?.data ?? d?.templates ?? []);
      }
      if (at.status === 'fulfilled') {
        const d = at.value.data;
        setAssetTypes(Array.isArray(d) ? d : d?.data ?? d?.assetTypes ?? []);
      }
      if (assetRes.status === 'fulfilled') {
        const d = assetRes.value.data;
        setAssets(Array.isArray(d) ? d : d?.data ?? d?.assets ?? []);
      }
      if (loc.status === 'fulfilled') {
        const d = loc.value.data;
        setLocations(Array.isArray(d) ? d : d?.data ?? d?.locations ?? []);
      }
      if (uRes.status === 'fulfilled') setUsers(unwrapList(uRes.value).map(u => normalizeUser(u as Record<string, unknown>)));
      if (teamRes.status === 'fulfilled') setTeams(unwrapList(teamRes.value).map(g => normalizeTeam(g as Record<string, unknown>)));
      if (jtRes.status === 'fulfilled') setJobTags(jtRes.value.data?.data ?? jtRes.value.data ?? []);
    } finally {
      setLoadingRef(false);
    }
  }, []);

  useEffect(() => { if (open) loadRefData(); }, [open, loadRefData]);

  // ── Populate form when editing ───────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      const normalizedLevels = (schedule.alert_levels ?? []).map(al => ({
        ...al,
        recipients: (al.recipients ?? []).map(r => {
          const raw = r as Record<string, unknown>;
          const rt = String(raw.recipient_type ?? '');
          if (rt === 'email' && raw.email) {
            return { ...r, recipient_type: 'email' as const, email: String(raw.email), _label: String(raw.email) };
          }
          return {
            ...r,
            _label: raw.user_id
              ? String(raw.user_name ?? 'User')
              : raw.team_id
                ? String(raw.team_name ?? 'Team')
                : String(raw.job_tag_name ?? 'Tag'),
          };
        }),
      }));
      setForm({
        ...emptyForm(),
        ...schedule,
        recurrence_type: (schedule as any).recurrence_type ?? 'weekly',
        day_of_month: (schedule as any).day_of_month ?? 1,
        week_of_month: (schedule as any).week_of_month ?? 1,
        weekday: (schedule as any).weekday ?? 1,
        interval_amount: (schedule as any).interval_amount ?? 2,
        interval_unit: (schedule as any).interval_unit ?? 'weeks',
        month_of_year: (schedule as any).month_of_year ?? 0,
        yearly_day: (schedule as any).yearly_day ?? 1,
        roundup_lookback_amount: schedule.roundup_lookback_amount ?? 24,
        roundup_lookback_unit: schedule.roundup_lookback_unit ?? 'hours',
        alert_levels: normalizedLevels,
        roundup_recipients: (schedule.roundup_recipients ?? []).map(r => {
          const raw = r as Record<string, unknown>;
          const rt = String(raw.recipient_type ?? '');
          if (rt === 'email' && raw.email) {
            return { ...r, recipient_type: 'email' as const, email: String(raw.email), _label: String(raw.email) };
          }
          return {
            ...r,
            _label: raw.user_id
              ? String(raw.user_name ?? 'User')
              : raw.team_id
                ? String(raw.team_name ?? 'Team')
                : String(raw.job_tag_name ?? 'Tag'),
          };
        }),
      });
      if ((schedule as any).scope_job_tag_ids?.length > 0) setAssetScope('asset_tag');
      else if (schedule.scope_asset_ids?.length > 0) setAssetScope('asset_list');
      else if (schedule.location_id && locationsEnabled) setAssetScope('location');
      else if ((schedule as any).asset_type_ids?.length > 0 || schedule.asset_type_id) setAssetScope('asset_type');
      else setAssetScope('all');
    } else {
      setForm(emptyForm());
      setAssetScope('asset_type');
    }
    setError('');
  }, [open, schedule, locationsEnabled]);

  // ── Field helpers ────────────────────────────────────────────────────────

  const set = <K extends keyof ScheduleData>(key: K, value: ScheduleData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleDay = (day: number) =>
    setForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort(),
    }));

  const addAlertLevel = () =>
    setForm(prev => ({
      ...prev,
      alert_levels: [...prev.alert_levels, { minutes_after_scheduled_time: 30, alert_type: 'warning', recipients: [] }],
    }));

  const removeAlertLevel = (idx: number) =>
    setForm(prev => ({ ...prev, alert_levels: prev.alert_levels.filter((_, i) => i !== idx) }));

  const updateAlertLevel = (idx: number, patch: Partial<AlertLevel>) =>
    setForm(prev => ({
      ...prev,
      alert_levels: prev.alert_levels.map((al, i) => i === idx ? { ...al, ...patch } : al),
    }));

  // ── Live timeline preview ─────────────────────────────────────────────

  const timelineItems = useMemo(() => {
    const base = form.time_of_day?.slice(0, 5) ?? '08:00';
    const [hStr, mStr] = base.split(':');
    const baseH = parseInt(hStr) || 0;
    const baseM = parseInt(mStr) || 0;
    const baseTotalMin = baseH * 60 + baseM;

    const addMin = (totalMin: number) => {
      const h = Math.floor(totalMin / 60) % 24;
      const m = totalMin % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const items: { time: string; label: string; color: string; isOpen?: boolean }[] = [
      { time: base, label: 'Window opens — checks can now be completed', color: '#2563EB', isOpen: true },
    ];

    const sorted = [...form.alert_levels].sort((a, b) => a.minutes_after_scheduled_time - b.minutes_after_scheduled_time);
    sorted.forEach((al, i) => {
      const typeOpt = ALERT_TYPE_OPTS.find(o => o.value === al.alert_type) ?? ALERT_TYPE_OPTS[0];
      const mins = al.minutes_after_scheduled_time;
      items.push({
        time: addMin(baseTotalMin + mins),
        label: i === 0
          ? `Deadline — ${typeOpt.label} alert if not done (+${mins} min)`
          : `${typeOpt.label} escalation (+${mins} min)`,
        color: typeOpt.color,
      });
    });

    return items;
  }, [form.time_of_day, form.alert_levels]);

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setError('');
    if (!form.schedule_name.trim()) { setError('Schedule name is required.'); return; }
    if (!form.template_id) { setError('Please select a check template.'); return; }
    if ((form.recurrence_type ?? 'weekly') === 'weekly' && form.days_of_week.length === 0) {
      setError('Select at least one day of the week.'); return;
    }
    if (assetScope === 'asset_type' && form.asset_type_ids.length === 0) { setError('Select at least one asset type.'); return; }
    if (assetScope === 'asset_list' && form.scope_asset_ids.length === 0) { setError('Select at least one asset.'); return; }
    if (assetScope === 'location' && !form.location_id) { setError('Select a location.'); return; }
    if (assetScope === 'asset_tag' && form.scope_job_tag_ids.length === 0) { setError('Select at least one asset tag.'); return; }

    const payload = {
      ...form,
      timezone: 'Europe/London',
      time_of_day: form.time_of_day.length === 5 ? `${form.time_of_day}:00` : form.time_of_day,
      scope_asset_ids: assetScope === 'asset_list' ? form.scope_asset_ids : [],
      asset_type_ids: assetScope === 'location' ? form.asset_type_ids : (assetScope === 'asset_type' ? form.asset_type_ids : []),
      location_id: assetScope === 'location' ? form.location_id : null,
      scope_job_tag_ids: assetScope === 'asset_tag' ? form.scope_job_tag_ids : [],
      scope_tag_template_ids: assetScope === 'asset_tag' ? (form.scope_tag_template_ids ?? []) : [],
      // 'all' scope: send a sentinel so the backend knows this is intentional
      asset_scope_all: assetScope === 'all' ? true : undefined,
    };

    setSaving(true);
    try {
      if (schedule?.schedule_id) {
        await axios.put(`${API_BASE_URL}/template-check-schedules/${schedule.schedule_id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_BASE_URL}/template-check-schedules`, payload, { headers: getAuthHeaders() });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save schedule.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const isNew = !schedule?.schedule_id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '94vh' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScheduleIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700}>{isNew ? 'New Schedule' : 'Edit Schedule'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Define when a check must be completed and who to alert if it's overdue.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {loadingRef ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

          {/* ── 1. Basics ── */}
          <SectionLabel>Basics</SectionLabel>
          <TextField
            label="Schedule name"
            value={form.schedule_name}
            onChange={e => set('schedule_name', e.target.value)}
            fullWidth sx={{ mb: 2 }} size="small"
            placeholder="e.g. Daily Pre-Use — Morning Shift"
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Check template *</InputLabel>
            <Select value={form.template_id} label="Check template *" onChange={e => set('template_id', e.target.value)}>
              {templates.map(t => <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />}
            label="Schedule enabled"
            sx={{ mb: 1 }}
          />

          <Divider sx={{ my: 2.5 }} />

          {/* ── 2. Asset Scope ── */}
          <SectionLabel>Which assets?</SectionLabel>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Choose which assets this schedule applies to. The check template above must be completed by the deadline for the assets below.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            {([
              { value: 'all' as const,        label: 'All assets',     desc: 'Every active asset in the business' },
              { value: 'asset_type' as const, label: 'By asset type',  desc: 'All active assets of the selected type(s)' },
              { value: 'asset_list' as const, label: 'Specific assets', desc: 'Pick individual assets by name' },
              ...(locationsEnabled ? [{ value: 'location' as const, label: `By ${locationLabel}`, desc: `All assets at a specific ${locationLabel.toLowerCase()}` }] : []),
              { value: 'asset_tag' as const,  label: 'By asset tag',   desc: 'Assets carrying a specific tag' },
            ]).map(opt => (
              <Box
                key={opt.value}
                onClick={() => setAssetScope(opt.value)}
                sx={{
                  flex: '1 1 140px', p: 1.5, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                  borderColor: assetScope === opt.value ? 'primary.main' : 'divider',
                  bgcolor: assetScope === opt.value ? 'primary.50' : 'background.paper',
                  transition: 'border-color .15s',
                }}
              >
                <Typography variant="body2" fontWeight={700} color={assetScope === opt.value ? 'primary.main' : 'text.primary'} sx={{ mb: 0.25 }}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Box>
            ))}
          </Box>

          {assetScope === 'asset_type' && (
            <Autocomplete
              multiple
              options={assetTypes}
              getOptionLabel={o => o.type_name}
              value={assetTypes.filter(t => form.asset_type_ids.includes(t.asset_type_id))}
              onChange={(_, v) => set('asset_type_ids', v.map(t => t.asset_type_id))}
              renderInput={params => <TextField {...params} label="Asset types" size="small" placeholder="Select types…" />}
              renderTags={(v, getTagProps) =>
                v.map((opt, i) => <Chip key={opt.asset_type_id} label={opt.type_name} size="small" {...getTagProps({ index: i })} />)
              }
              sx={{ mb: 2 }}
            />
          )}

          {assetScope === 'asset_list' && (
            <Autocomplete
              multiple
              options={assets}
              getOptionLabel={o => `${o.asset_name}${o.serial_number ? ` · ${o.serial_number}` : ''}${o.location_name ? ` (${o.location_name})` : ''}`}
              value={assets.filter(a => form.scope_asset_ids.includes(a.asset_id))}
              onChange={(_, v) => set('scope_asset_ids', v.map(a => a.asset_id))}
              renderInput={params => <TextField {...params} label="Select assets" size="small" placeholder="Search by name or serial…" />}
              renderTags={(v, getTagProps) =>
                v.map((opt, i) => <Chip key={opt.asset_id} label={opt.asset_name} size="small" {...getTagProps({ index: i })} />)
              }
              sx={{ mb: 2 }}
            />
          )}

          {assetScope === 'asset_tag' && (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                options={jobTags.filter((t: any) => t.tag_scope !== 'user')}
                getOptionLabel={(o: any) => o.tag_name}
                value={jobTags.filter((t: any) => (form.scope_job_tag_ids ?? []).includes(t.job_tag_id))}
                onChange={(_, v) => set('scope_job_tag_ids', (v as any[]).map(t => t.job_tag_id))}
                renderInput={params => <TextField {...params} label="Asset tags *" size="small" placeholder="Search tags…" />}
                renderTags={(v, getTagProps) =>
                  (v as any[]).map((opt, i) => <Chip key={opt.job_tag_id} label={opt.tag_name} size="small" {...getTagProps({ index: i })} />)
                }
              />
              {templates.length > 0 && (
                <Autocomplete
                  multiple
                  options={templates}
                  getOptionLabel={o => o.template_name}
                  value={templates.filter(t => (form.scope_tag_template_ids ?? []).includes(t.template_id))}
                  onChange={(_, v) => set('scope_tag_template_ids', v.map(t => t.template_id))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Filter by template (optional)"
                      size="small"
                      placeholder="Any template…"
                      helperText="Only include assets whose last check used one of these templates"
                    />
                  )}
                  renderTags={(v, getTagProps) =>
                    v.map((opt, i) => <Chip key={opt.template_id} label={opt.template_name} size="small" {...getTagProps({ index: i })} />)
                  }
                />
              )}
            </Stack>
          )}

          {assetScope === 'location' && locationsEnabled && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{locationLabel}</InputLabel>
                <Select
                  value={form.location_id ?? ''}
                  label={locationLabel}
                  onChange={e => set('location_id', e.target.value || null)}
                >
                  <MenuItem value=""><em>All locations</em></MenuItem>
                  {locations.map((l: any) => {
                    const id = l.locationId ?? l.location_id;
                    const name = l.locationName ?? l.location_name;
                    return <MenuItem key={id} value={id}>{name}</MenuItem>;
                  })}
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                sx={{ flex: 1 }}
                options={assetTypes}
                getOptionLabel={o => o.type_name}
                value={assetTypes.filter(t => form.asset_type_ids.includes(t.asset_type_id))}
                onChange={(_, v) => set('asset_type_ids', v.map(t => t.asset_type_id))}
                renderInput={params => <TextField {...params} label="Filter by type (optional)" size="small" />}
                renderTags={(v, gtp) => v.map((o, i) => <Chip key={o.asset_type_id} label={o.type_name} size="small" {...gtp({ index: i })} />)}
              />
            </Box>
          )}

          <Divider sx={{ my: 2.5 }} />

          {/* ── 3. Completion window ── */}
          <SectionLabel>Completion window</SectionLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set <strong>when the check window opens</strong> and <strong>how long people have</strong> to complete it.
            The window opens at the time you set below. The first alert below defines the deadline — how many minutes
            from window-open before it's considered overdue. Further alerts are escalations after the deadline passes.
          </Typography>

          {/* Recurrence type cards */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>Recurrence</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
            {RECURRENCE_OPTS.map(opt => {
              const active = (form.recurrence_type ?? 'weekly') === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => set('recurrence_type', opt.value)}
                  sx={{
                    p: 1.25, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'primary.50' : 'background.paper',
                    transition: 'border-color .15s, background-color .15s',
                    userSelect: 'none',
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color={active ? 'primary.main' : 'text.primary'} sx={{ display: 'block' }}>
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {opt.desc}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ── Weekly: day chips ── */}
          {(form.recurrence_type ?? 'weekly') === 'weekly' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>Which days</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {DAY_LABELS.map((d, i) => (
                  <Chip
                    key={d} label={d}
                    onClick={() => toggleDay(i)}
                    color={form.days_of_week.includes(i) ? 'primary' : 'default'}
                    variant={form.days_of_week.includes(i) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', minWidth: 46 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* ── Monthly (date): day-of-month select ── */}
          {form.recurrence_type === 'monthly_date' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Day of month</InputLabel>
                <Select
                  value={form.day_of_month ?? 1}
                  label="Day of month"
                  onChange={e => set('day_of_month', Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <MenuItem key={d} value={d}>{ordinal(d)} of the month</MenuItem>
                  ))}
                  <MenuItem value={-1}>Last day of the month</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* ── Monthly (weekday): occurrence + day selects ── */}
          {form.recurrence_type === 'monthly_weekday' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Which occurrence</InputLabel>
                <Select
                  value={form.week_of_month ?? 1}
                  label="Which occurrence"
                  onChange={e => set('week_of_month', Number(e.target.value))}
                >
                  <MenuItem value={1}>First</MenuItem>
                  <MenuItem value={2}>Second</MenuItem>
                  <MenuItem value={3}>Third</MenuItem>
                  <MenuItem value={4}>Fourth</MenuItem>
                  <MenuItem value={-1}>Last</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Day of week</InputLabel>
                <Select
                  value={form.weekday ?? 1}
                  label="Day of week"
                  onChange={e => set('weekday', Number(e.target.value))}
                >
                  {FULL_DAY_NAMES.map((d, i) => (
                    <MenuItem key={d} value={i}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* ── Every N: interval inputs ── */}
          {form.recurrence_type === 'interval' && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Every</Typography>
              <TextField
                size="small"
                type="number"
                value={form.interval_amount ?? 2}
                onChange={e => set('interval_amount', Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{ width: 80 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={form.interval_unit ?? 'weeks'}
                  onChange={e => set('interval_unit', e.target.value as ScheduleData['interval_unit'])}
                >
                  <MenuItem value="days">days</MenuItem>
                  <MenuItem value="weeks">weeks</MenuItem>
                  <MenuItem value="months">months</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* ── Yearly: month + day selects ── */}
          {form.recurrence_type === 'yearly' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={form.month_of_year ?? 0}
                  label="Month"
                  onChange={e => set('month_of_year', Number(e.target.value))}
                >
                  {MONTH_NAMES_FULL.map((m, i) => (
                    <MenuItem key={m} value={i}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Day of month</InputLabel>
                <Select
                  value={form.yearly_day ?? 1}
                  label="Day of month"
                  onChange={e => set('yearly_day', Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <MenuItem key={d} value={d}>{ordinal(d)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Window opens at"
              type="time"
              value={form.time_of_day.slice(0, 5)}
              onChange={e => set('time_of_day', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 180 }}
              helperText="Checks can be completed from this time onwards"
            />
          </Box>

          {/* Live timeline */}
          {timelineItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#F8FAFC' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ClockIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: .4 }}>
                  Timeline preview
                </Typography>
              </Box>
              <Stack spacing={0.75}>
                {timelineItems.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      minWidth: 44, height: 22,
                      bgcolor: item.color + '18',
                      border: `1px solid ${item.color}40`,
                      borderRadius: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: item.color, fontSize: '0.65rem', fontFamily: 'monospace' }}>
                        {item.time}
                      </Typography>
                    </Box>
                    {i < timelineItems.length - 1 && (
                      <ArrowIcon sx={{ fontSize: 14, color: 'text.disabled', mt: 0.4, flexShrink: 0, display: 'none' }} />
                    )}
                    <Typography variant="caption" color={item.isOpen ? 'primary.main' : 'text.secondary'} fontWeight={item.isOpen ? 600 : 400} sx={{ pt: 0.25 }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          <Divider sx={{ my: 2.5 }} />

          {/* ── 4. Alert Escalation ── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <SectionLabel>Overdue alerts & escalation</SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                The <strong>first level</strong> defines the deadline — how many minutes from window-open before a check is overdue.
                Add more levels to escalate if still not done. Each level notifies its own set of people.
              </Typography>
            </Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addAlertLevel} variant="outlined" sx={{ flexShrink: 0, ml: 2, mt: 0.5 }}>
              Add level
            </Button>
          </Box>

          {form.alert_levels.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2, mt: 1.5 }}>
              No alert levels set. Add at least one level to define when the check becomes overdue and who gets notified.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mb: 2, mt: 1.5 }}>
              {form.alert_levels
                .slice()
                .sort((a, b) => a.minutes_after_scheduled_time - b.minutes_after_scheduled_time)
                .map((al, idx) => {
                  const realIdx = form.alert_levels.indexOf(al);
                  const typeOpt = ALERT_TYPE_OPTS.find(o => o.value === al.alert_type) ?? ALERT_TYPE_OPTS[0];
                  const isDeadline = idx === 0;
                  return (
                    <Paper
                      key={realIdx}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, borderColor: typeOpt.color + '50', bgcolor: typeOpt.bg + '30' }}
                    >
                      {/* Level header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <AlertLevelIcon sx={{ fontSize: 18, color: typeOpt.color }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: typeOpt.color }}>
                            {isDeadline ? '⏱ Deadline (first alert)' : `Escalation level ${idx + 1}`}
                          </Typography>
                          {isDeadline && (
                            <Typography variant="caption" color="text.secondary">
                              This is the main deadline — how long from window-open before the check is overdue
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Remove this level">
                          <IconButton size="small" color="error" onClick={() => removeAlertLevel(realIdx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Timing + type */}
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label={isDeadline ? 'Complete within (minutes)' : 'Escalate after (minutes from open)'}
                          type="number"
                          value={al.minutes_after_scheduled_time}
                          onChange={e => updateAlertLevel(realIdx, { minutes_after_scheduled_time: Math.max(1, parseInt(e.target.value) || 1) })}
                          size="small"
                          inputProps={{ min: 1 }}
                          sx={{ width: 220 }}
                          helperText={
                            isDeadline
                              ? `Deadline: ${al.minutes_after_scheduled_time} min after ${form.time_of_day?.slice(0, 5) ?? '—'}`
                              : `Alert fires ${al.minutes_after_scheduled_time} min after window opens`
                          }
                        />
                        <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                          <InputLabel>Alert severity</InputLabel>
                          <Select
                            value={al.alert_type}
                            label="Alert severity"
                            onChange={e => updateAlertLevel(realIdx, { alert_type: e.target.value as AlertLevel['alert_type'] })}
                          >
                            {ALERT_TYPE_OPTS.map(o => (
                              <MenuItem key={o.value} value={o.value}>
                                <Chip label={o.label} size="small" sx={{ bgcolor: o.bg, color: o.color, fontWeight: 700 }} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Recipients */}
                      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <RecipientPicker
                          label={isDeadline ? 'Who gets notified when overdue?' : 'Who gets this escalation?'}
                          recipients={al.recipients}
                          onChange={recs => updateAlertLevel(realIdx, { recipients: recs })}
                          users={users}
                          teams={teams}
                          jobTags={jobTags}
                          emptyHint="No recipients at this level — the schedule still runs, but no one is notified for this step."
                        />
                      </Box>
                    </Paper>
                  );
                })}
            </Stack>
          )}

          <Divider sx={{ my: 2.5 }} />

          {/* ── 5. Notifications channels ── */}
          <SectionLabel>Notification channels</SectionLabel>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.email_notifications_enabled} onChange={e => set('email_notifications_enabled', e.target.checked)} />}
              label="Email notifications"
            />
            <FormControlLabel
              control={<Switch checked={form.push_notifications_enabled} onChange={e => set('push_notifications_enabled', e.target.checked)} />}
              label="Push notifications (mobile app)"
            />
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {/* ── 6. Round-up ── */}
          <SectionLabel>Round-up summary email</SectionLabel>
          <FormControlLabel
            control={<Switch checked={form.send_roundup_email} onChange={e => set('send_roundup_email', e.target.checked)} />}
            label="Send round-up email (at completion or after final escalation)"
            sx={{ mb: form.send_roundup_email ? 1.5 : 0 }}
          />

          {form.send_roundup_email && (
            <>
              <Box sx={{ ml: 0, mt: 1, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Look back</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={form.roundup_lookback_amount}
                  onChange={e => set('roundup_lookback_amount', Math.max(1, Number(e.target.value)))}
                  inputProps={{ min: 1, style: { width: 52, padding: '4px 6px' } }}
                  sx={{ width: 70 }}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select value={form.roundup_lookback_unit} onChange={e => set('roundup_lookback_unit', e.target.value as ScheduleData['roundup_lookback_unit'])} sx={{ fontSize: '0.8rem' }}>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <RecipientPicker
                  label="Who receives the round-up email?"
                  recipients={form.roundup_recipients}
                  onChange={recs => set('roundup_recipients', recs)}
                  users={users}
                  teams={teams}
                  jobTags={jobTags}
                  emptyHint="No recipients — enable round-up recipients here or the summary email will not be sent."
                />
              </Box>
            </>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, flexShrink: 0 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ flex: 1 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loadingRef} sx={{ flex: 2 }}>
          {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : isNew ? 'Create Schedule' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Small helper ─────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
    {children}
  </Typography>
);

export default CommScheduleDrawer;
